<script setup lang="ts">
import { GpBrandLoading, GpFadeTransition, GpTabBar } from '@gportal/uikit';
import type { ServiceError } from 'gpcore-apikit/grpc/types';
import { useImageStore } from 'gpcore-apikit/store/pinia/image';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { type Flavour } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import {
  type BasicImage,
  type Image,
  type OperatingSystem,
} from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { CloudProviderType } from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import OrderSection from '../OrderSection.vue';
import ImageTabProject from './ImageTabProject.vue';
import ImageTabPublic from './ImageTabPublic.vue';
import useProjectImages from './composables/useProjectImages';
import usePublicImages from './composables/usePublicImages';

export interface Props {
  installedImage?: Image;
  cloudProviderType?: CloudProviderType;
  preTitle?: string;
  title?: string;
  flavour?: Flavour;
  forcePreselect?: boolean;
}

const {
  preTitle = null,
  installedImage = null,
  cloudProviderType = null,
  title = null,
  flavour = null,
  forcePreselect = false,
} = defineProps<Props>();

const image = defineModel<BasicImage>('modelValue');

const isValid = ref(false);

const selectedTab = ref<'public' | 'project'>('public');
const handleTabChange = async (newTab) => {
  if (selectedTab.value === newTab) return;
  selectedTab.value = newTab;
};

const imageStore = useImageStore();
const { findPublicImageFromSource } = usePublicImages();
const { findProjectImageFromSource } = useProjectImages();

const projectStore = useProjectStore();
const currentProject = computed(() => projectStore.projectId);
const isLoadingImages = ref(true);
const { addNotification } = useNotifications();

// if we have an installed image, we try to fetch all images and assign them, for re-install view only!
const presetImage = async (): Promise<BasicImage> => {
  if (installedImage == null) {
    isLoadingImages.value = false;
    return null;
  }

  try {
    let imagesSource: Array<OperatingSystem> | Array<BasicImage> = [];
    if (imageStore.publicImages.length === 0) {
      isLoadingImages.value = true;
      const response = await imageStore.reloadPublicImages(cloudProviderType);
      imagesSource = response.operatingSystems as Array<OperatingSystem>;
    }

    let img = findPublicImageFromSource(installedImage, imagesSource as Array<OperatingSystem>);
    if (img != null) {
      return img;
    }

    if (imageStore.projectImages.length === 0) {
      isLoadingImages.value = true;
      const response = await imageStore.reloadProjectImages(currentProject.value, true);
      imagesSource = response.images as Array<BasicImage>;
    }

    img = findProjectImageFromSource(installedImage, imagesSource as Array<BasicImage>);
    if (img != null) {
      selectedTab.value = 'project';
    }

    return img;
  } catch (error) {
    addNotification({
      message: (error as ServiceError).message,
      type: 'danger',
    });
  } finally {
    isLoadingImages.value = false;
  }

  return null;
};

onMounted(async () => {
  image.value = await presetImage();
});

onUnmounted(() => {
  image.value = null;
});
</script>

<template>
  <OrderSection
    :pre-title="preTitle"
    :title="title"
    :is-loading="isLoadingImages"
    :is-done="isValid"
    section-key="image"
    v-playwright="'nodes-create-image'"
  >
    <template v-if="isLoadingImages">
      <GpBrandLoading class="tw-mx-auto tw-mt-4" message="Loading images..." />
    </template>
    <template v-else>
      <GpTabBar
        class="tw-mt-4 !tw-text-gp-mouse"
        :model-value="selectedTab"
        :overlay-breakpoint="null"
        :tabs-model="[
          { id: 'public', label: 'Public' },
          { id: 'project', label: 'Project' },
        ]"
        @update:model-value="handleTabChange"
      >
        <template #item="{ item, isActive }">
          <a
            class="tw-font-bold"
            :class="{
              'tw-cursor-pointer tw-text-gp-mouse hover:tw-text-gp-cyan': !isActive,
              'tw-cursor-default tw-text-gp-cyan': isActive,
            }"
            @click="handleTabChange(item.id)"
            v-playwright="`nodes-create-image-tab-${item.id}`"
            :aria-selected="isActive"
          >
            {{ item.label }}
          </a>
        </template>
      </GpTabBar>
      <GpFadeTransition mode="out-in">
        <template v-if="selectedTab === 'public'">
          <ImageTabPublic
            :installed-image="installedImage"
            :cloud-provider-type="cloudProviderType"
            :flavour="flavour"
            v-model="image"
            v-model:is-valid="isValid"
            :force-preselect="forcePreselect"
          />
        </template>
        <!-- project images -->
        <template v-else-if="selectedTab === 'project'">
          <ImageTabProject
            :installed-image="installedImage"
            :force-preselect="forcePreselect"
            v-model="image"
            v-model:is-valid="isValid"
          />
        </template>
      </GpFadeTransition>
    </template>
  </OrderSection>
</template>
